import type { Language } from "@talentkontor/domain";
import type { Api } from "../../../data/api";
import { nameIn, t, uiLocale } from "../../../lib/sprache";

/**
 * Vertragsmuster (Anwalt Punkt 13, Zusatz 24/25; 0181): CreatorOffice-Vorlagen sind unverbindliche Muster. Kennung
 * „Kooperationsvertrag DE · Version 1 · Stand Oktober 2026“ aus platform_contract_templates (version, jurisdiction, legal_as_of);
 * ein Muster nach deutschem Recht wird Konten in Österreich oder der Schweiz nicht als passend dargestellt. Reine Logik ohne React.
 */

export interface MusterZeile {
  key: string;
  language: Language;
  name: string;
  kind_name_de: string;
  kind_name_en: string;
  version: number;
  jurisdiction: string | null;
  legal_as_of: string | null;
  /** R-07: kurz, was sich gegenüber der vorigen Fassung geändert hat (0491); fehlt bei älteren Datenbanken */
  change_note?: string | null;
}

export const loadMusterZeilen = (a: Pick<Api, "select">) => a.select<MusterZeile>("platform_contract_templates", {
  columns: "key, language, name, kind_name_de, kind_name_en, version, jurisdiction, legal_as_of, change_note",
});

/**
 * R-07 (Umbauten 03.11.2026): der Hinweis „neuere Fassung“ lässt sich je Muster und Versionssprung einmal wegklicken; gemerkt
 * im Browser (localStorage), damit er nicht bei jedem Vertrag aus derselben Vorlage wieder erscheint. Ein weiterer Sprung
 * (Version 3) zeigt ihn erneut.
 */
export function musterHinweisSchluessel(z: Pick<MusterZeile, "key" | "language" | "version">, entwurfVersion: number): string {
  return `tk-muster-neu-weg:${z.key}:${z.language}:${entwurfVersion}->${z.version}`;
}
export function musterHinweisWeg(schluessel: string, speicher: Pick<Storage, "getItem"> | null = typeof localStorage === "undefined" ? null : localStorage): boolean {
  try { return speicher?.getItem(schluessel) === "1"; } catch { return false; }
}
export function musterHinweisMerken(schluessel: string, speicher: Pick<Storage, "setItem"> | null = typeof localStorage === "undefined" ? null : localStorage): void {
  try { speicher?.setItem(schluessel, "1"); } catch { /* Speicher gesperrt: der Hinweis kommt beim nächsten Mal wieder */ }
}

/** Muster zu einer Vorlage der Agentur (übernommen aus der Galerie: platform_version gesetzt) */
export function musterZuVorlage(zeilen: readonly MusterZeile[] | undefined, vorlage: { key: string; language: string; platform_version?: number | null } | null | undefined): MusterZeile | null {
  if (!vorlage || vorlage.platform_version == null) return null;
  return (zeilen ?? []).find((z) => z.key === vorlage.key && z.language === vorlage.language) ?? (zeilen ?? []).find((z) => z.key === vorlage.key) ?? null;
}

/** „Kooperationsvertrag DE · Version 1 · Stand Oktober 2026“ (Version der übernommenen Fassung, wenn bekannt) */
export function musterKennung(z: Pick<MusterZeile, "kind_name_de" | "kind_name_en" | "version" | "jurisdiction" | "legal_as_of">, version?: number | null): string {
  const art = nameIn(z.kind_name_de, z.kind_name_en);
  const stand = z.legal_as_of
    ? new Intl.DateTimeFormat(uiLocale(), { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${z.legal_as_of.slice(0, 10)}T00:00:00Z`))
    : null;
  return [`${art}${z.jurisdiction ? ` ${z.jurisdiction}` : ""}`, t(`Version ${version ?? z.version}`, `Version ${version ?? z.version}`), stand ? t(`Stand ${stand}`, `As of ${stand}`) : null]
    .filter(Boolean).join(" · ");
}

export const musterHinweisText = () => t(
  "Unverbindliches Vertragsmuster, nicht auf deinen Fall zugeschnitten und keine Rechtsberatung. Prüfe vor dem Einsatz, ob es zu deiner Situation passt, bei Bedarf mit eigener rechtlicher Beratung.",
  "Non-binding sample contract, not tailored to your case and not legal advice. Before using it, check whether it fits your situation, with your own legal advice if needed.");

/** Hinweis, wenn das Muster für eine andere Rechtsordnung erstellt ist als das Sitzland des Kontos (z. B. DE-Muster, Konto in AT/CH) */
export function musterLandHinweis(jurisdiction: string | null | undefined, orgCountry: string | null | undefined): string | null {
  const j = (jurisdiction ?? "").toUpperCase();
  const c = (orgCountry ?? "").toUpperCase();
  if (!j || !c || j === c) return null;
  // K-414 (0292): für Deutschland, Österreich und die Schweiz formuliert; Recht, Gerichtsstand und Steuerwort aus den Firmenangaben
  if (j === "DACH") {
    if (["DE", "AT", "CH"].includes(c)) return null;
    const anders = c === "LI" ? t("Liechtenstein", "Liechtenstein") : c;
    return t(`Dieses Muster ist für Deutschland, Österreich und die Schweiz formuliert und nicht für ${anders} geprüft. Vor dem Einsatz bitte rechtlich prüfen und anpassen lassen.`,
      `This sample is drafted for Germany, Austria and Switzerland and has not been reviewed for ${anders}. Please have it legally reviewed and adapted before use.`);
  }
  const recht = j === "DE" ? t("deutschem Recht", "German law") : j === "AT" ? t("österreichischem Recht", "Austrian law") : j === "CH" ? t("Schweizer Recht", "Swiss law") : j;
  const land = c === "AT" ? t("Österreich", "Austria") : c === "CH" ? t("die Schweiz", "Switzerland") : c === "LI" ? t("Liechtenstein", "Liechtenstein") : c === "DE" ? t("Deutschland", "Germany") : c;
  return t(`Dieses Muster ist nach ${recht} erstellt und nicht für ${land} geprüft. Vor dem Einsatz bitte rechtlich prüfen und anpassen lassen.`,
    `This sample was drafted under ${recht} and has not been reviewed for ${land}. Please have it legally reviewed and adapted before use.`);
}


/**
 * Österreich-/Schweiz-Prüfung K-247, Neufassung 0276 (Vertragsprüfung 15.10.2026), Kennzeichnung DACH (0292, K-414): Das
 * Vertragspaket (pro_*) ist für Deutschland, Österreich und die Schweiz formuliert (Recht, Gerichtsstand und Steuerwort aus den
 * Firmenangaben, § 37b EStG nur im Zweig deutsches Recht, Art. 404 OR im Zweig Schweizer Recht). Ein Hinweis mit Bestätigung nur
 * noch für Konten außerhalb dieser drei Länder (z. B. Liechtenstein). null = kein Hinweis nötig.
 */
export function paketFremdesRecht(templateKey: string | null | undefined, orgCountry: string | null | undefined): string | null {
  const c = (orgCountry ?? "").toUpperCase();
  if (!templateKey?.startsWith("pro_") || !c || ["DE", "AT", "CH"].includes(c)) return null;
  const land = c === "LI" ? t("Liechtenstein", "Liechtenstein") : c;
  return t(`Dieses Vertragspaket ist für Deutschland, Österreich und die Schweiz formuliert. Recht, Gerichtsstand und Steuerwort kommen aus euren Firmenangaben. Für ${land} ist es nicht im Einzelnen geprüft. Bitte vor dem Einsatz rechtlich prüfen und bei Bedarf anpassen lassen.`,
    `This contract pack is drafted for Germany, Austria and Switzerland. Governing law, place of jurisdiction and the tax term come from your company details. It has not been reviewed in detail for ${land}. Please have it legally reviewed and adapted where necessary before use.`);
}

export const paketFremdesRechtBestaetigung = () => t("Mir ist bewusst, dass dieses Paket für Deutschland, Österreich und die Schweiz formuliert ist. Ich lasse es vor dem Einsatz für unser Land prüfen und anpassen.",
  "I understand that this pack is drafted for Germany, Austria and Switzerland. I will have it reviewed and adapted for our country before use.");
